import { customFieldsToSchema, formatFieldValue, getFieldValue, numericValue, dateValue } from './formSchema';

const EMPTY = '__wt_empty__';
const normalize = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('it').trim();
const scalar = (value) => !(value instanceof Date) && value && typeof value === 'object' ? value.label ?? value.name ?? value.fullName ?? value.displayName ?? value.id ?? '' : value;
export function fieldValues(row, field) {
  const accessor = field.filter?.accessor ?? field.table?.accessor ?? field.name;
  const collection = ['clientId', 'supplierId', 'staffId'].includes(field.name) && typeof accessor === 'string' && accessor === field.name
    ? row[`${field.name}s`] : undefined;
  const raw = Array.isArray(collection) ? collection : getFieldValue(row, accessor);
  return (Array.isArray(raw) ? raw : [raw]).map(scalar).filter((value) => value !== undefined && value !== null && value !== '');
}

/** Project the same form metadata used by forms/tables, including hidden table fields. */
export function filtersFromFormSchemas(schemas, { rows = [], customFields = [], relations = {} } = {}) {
  const unique = new Map();
  [...(Array.isArray(schemas) ? schemas : [schemas]).flatMap((schema) => schema?.fields || []), ...customFieldsToSchema(customFields)]
    .filter((field) => field.filter !== false && !field.sensitive && !['password', 'hidden', 'file'].includes(field.type))
    .forEach((field) => { if (!unique.has(field.name)) unique.set(field.name, field); });
  return [...unique.values()].map((field) => {
    const type = String(field.type || 'text').toLowerCase();
    const kind = ['date', 'datetime', 'datetime-local'].includes(type) ? 'date-range'
      : ['number', 'currency'].includes(type) ? 'number-range'
        : type === 'textarea' ? 'text' : 'options';
    const options = new Map();
    (field.options || []).forEach((option) => {
      const value = option?.value ?? option;
      options.set(String(value), { value: String(value), label: String(option?.label ?? value) });
    });
    if (!field.filter?.accessor && !field.table?.accessor) {
      (relations[field.name] || []).forEach((record) => options.set(String(record.id), {
        value: String(record.id), label: record.name || record.fullName || record.displayName || record.legalName || record.company || String(record.id),
      }));
    }
    rows.forEach((row) => fieldValues(row, field).forEach((value) => {
      const key = String(value);
      if (!options.has(key)) options.set(key, { value: key, label: formatFieldValue(value, field) });
    }));
    if (type === 'boolean' || type === 'checkbox') {
      options.set('true', { value: 'true', label: 'Sì' });
      options.set('false', { value: 'false', label: 'No' });
    }
    return {
      key: `form:${field.name}`, label: field.label, selection: 'multi', emptyValue: [],
      kind, field, options: [
        ...[...options.values()].sort((a, b) => a.label.localeCompare(b.label, 'it', { numeric: true })),
        { value: EMPTY, label: 'Non indicato' },
      ],
    };
  });
}
export function isFilterActive(value, group = {}) {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Boolean(value.min || value.min === 0 || value.max || value.max === 0);
  return String(value ?? group.emptyValue ?? group.allValue ?? '') !== String(group.emptyValue ?? group.allValue ?? '');
}
export function filterModuleRows(rows = [], groups = [], values = {}) {
  const active = groups.filter((group) => isFilterActive(values[group.key], group));
  if (!active.length) return rows;
  return rows.filter((row) => active.every((group) => {
    const selected = values[group.key];
    const candidates = fieldValues(row, group.field);
    if (group.kind === 'text') return candidates.some((value) => normalize(value).includes(normalize(selected)));
    if (group.kind === 'date-range' || group.kind === 'number-range') {
      const number = group.kind === 'date-range' ? (value) => {
        const date = dateValue(value);
        return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() : null;
      } : numericValue;
      const min = number(selected.min), max = number(selected.max);
      return candidates.some((value) => {
        const n = number(value);
        return n !== null && (min === null || n >= min) && (max === null || n <= max);
      });
    }
    const chosen = Array.isArray(selected) ? selected.map(String) : [String(selected)];
    return (!candidates.length && chosen.includes(EMPTY)) || candidates.some((value) => chosen.includes(String(value)));
  }));
}

/** Existing module-specific filters take precedence over equivalent form filters. */
export function mergeFilterGroups(existing = [], generated = []) {
  const identity = (key = '') => key.replace(/^selected/, '').replace(/Filter$/, '').replace(/^form:/, '').toLocaleLowerCase('it');
  const keys = new Set(existing.map((group) => identity(group.formField || group.key)));
  const labels = new Set(existing.map((group) => normalize(group.label)));
  return [...existing, ...generated.filter((group) => !keys.has(identity(group.field.name)) && !labels.has(normalize(group.label)))];
}
